import _ from 'lodash';
import BenefitsApi from '@/api/BenefitsApi';
import PromotionsApi from '@/api/PromotionsApi';
import Segment from '@/Segment';
import promotions from '@/constants/promotions';
import { SUBSCRIPTION_TYPES } from '@/constants/subscription-state';
import benefitPricing from '@/store/utilities/benefit-pricing';
import money from '@/utils/money';

const LIFETIME_FIXED_PRICE = 390;

let heverBenefitRequestId = 0;

const state = {
  benefitId: null,
  benefitType: null,
  benefitAmount: null,
  benefitPromotion: null,
  benefitPercentage: null,
  // In shekels, only set for fixed-price
  benefitPrice: null,
  // Null means monthly, which is what every benefit granted before annual pricing is.
  subscriptionType: null,
  registeredUsingHeverPromotion: false,
  redeemed: false,
  isAppleBenefit: false,
  // Stops the two places that raise the benefit popup (PopupsManager during a trial, the
  // subscription flow otherwise) from both showing it.
  benefitPresented: false,
  hasHeverBenefit: null,
};

const getters = {
  BENEFIT_TYPES: state => promotions.BENEFIT_TYPES,
  BENEFIT_CREATION_STATUS: state => promotions.BENEFIT_CREATION_STATUS,
  // A benefit that states its plan is taken at its word; older ones fall back to their duration.
  isYearlyBenefit: state => benefitPricing.isYearlyPlan({
    subscriptionType: state.subscriptionType,
    benefitAmount: state.benefitAmount,
    benefitPrice: state.benefitPrice,
  }),
  // Distinct from isYearlyBenefit: only an explicit statement changes the wording to years, since a
  // legacy 12-month benefit is still described in months.
  declaresYearlyPlan: state => state.subscriptionType === SUBSCRIPTION_TYPES.YEARLY,
  isFixedPriceBenefit: state => state.benefitType === promotions.BENEFIT_TYPES.FIXED_PRICE,
  isLifetimeFixedPriceBenefit: (state, getters) => getters.isFixedPriceBenefit && state.benefitPrice === LIFETIME_FIXED_PRICE,
  hasUnredeemedBenefit: state => Boolean(state.benefitId) && !state.redeemed,
  benefitPlanType: (state, getters) => {
    if (!getters.hasUnredeemedBenefit) {
      return null;
    }
    return getters.isYearlyBenefit ? SUBSCRIPTION_TYPES.YEARLY : SUBSCRIPTION_TYPES.MONTHLY;
  },
  activeBenefitPricing: (state, getters, rootState) => (getters.hasUnredeemedBenefit
    ? benefitPricing.planPricing({
      monthlyPrice: rootState.subscription?.pricing?.monthly,
      annualPrice: rootState.subscription?.pricing?.annual,
      benefitPercentage: state.benefitPercentage,
      benefitAmount: state.benefitAmount,
      benefitPrice: state.benefitPrice,
      subscriptionType: state.subscriptionType,
    })
    : null),
  listPriceForPeriod: (state, getters, rootState) => (getters.isYearlyBenefit
    ? rootState.subscription?.pricing?.annual
    : rootState.subscription?.pricing?.monthly),
  benefitPeriodPrice: (state, getters) => getters.activeBenefitPricing?.price ?? getters.listPriceForPeriod,
  // Kept as the name the existing call sites use
  discountedPrice: (state, getters) => getters.benefitPeriodPrice,
  // The benefit still applies: the monthly one runs first and the subscription moves to annual
  // after it, which is what cashflow-home's getNextSubscriptionType does.
  benefitBelongsToOtherPlan: (state, getters, rootState) => {
    const selectedType = rootState.subscription?.type;
    if (!getters.hasUnredeemedBenefit || !selectedType || !getters.benefitPlanType) {
      return false;
    }
    return getters.benefitPlanType !== selectedType;
  },
  isPercentageDiscountBenefit: state => state.benefitType === promotions.BENEFIT_TYPES.PERCENTAGE_DISCOUNT,
  benefitPeriodsText: (state, getters) => {
    if (!getters.declaresYearlyPlan) {
      return getters.monthsText;
    }
    switch (state.benefitAmount) {
    case 1:
      return 'שנה';
    case 2:
      return 'שנתיים';
    default:
      return `${state.benefitAmount} שנים`;
    }
  },
  // What the customer got, in the terms of the plan the benefit prices: a yearly plan is stated as
  // the saving, since a per-month figure would read as a monthly subscription.
  benefitHeadline: (state, getters) => {
    if (!getters.hasUnredeemedBenefit) {
      return null;
    }
    // A yearly percentage states the percentage and the period, so it needs no price and is right
    // from the first render even where the page has not fetched pricing.
    if (getters.declaresYearlyPlan && state.benefitPercentage) {
      return `${state.benefitPercentage}% הנחה ל${getters.benefitPeriodsText}`;
    }
    const pricing = getters.activeBenefitPricing;
    if (!pricing) {
      return null;
    }
    if (!getters.declaresYearlyPlan) {
      return `${getters.monthsText} ב־${money.formatAmountKeepingAgorot(pricing.monthlyPrice)} ₪ לחודש`;
    }
    if (getters.isLifetimeFixedPriceBenefit) {
      return pricing.fullPrice
        ? `${money.formatAmountKeepingAgorot(pricing.fullPrice - pricing.price)} ₪ הנחה`
        : `${money.formatAmountKeepingAgorot(pricing.price)} ₪ לשנה`;
    }
    // A one-year benefit prices only the first year, since the renewal is at the list price.
    const period = state.benefitAmount === 1 ? 'שנה הראשונה' : getters.benefitPeriodsText;
    return pricing.fullPrice
      ? `${money.formatAmountKeepingAgorot(pricing.fullPrice - pricing.price)} ₪ הנחה ל${period}`
      : `${money.formatAmountKeepingAgorot(pricing.price)} ₪ ל${period}`;
  },
  // One shape for every benefit banner event, so they stay comparable across surfaces
  benefitEventProperties: (state, getters) => ({
    benefitType: state.benefitType,
    benefitPlanType: getters.benefitPlanType,
    statedSubscriptionType: state.subscriptionType ?? null,
    benefitAmount: state.benefitAmount,
    benefitPercentage: state.benefitPercentage ?? null,
    benefitPrice: state.benefitPrice ?? null,
    promotion: state.benefitPromotion,
  }),
  monthsText: state => {
    switch (state.benefitAmount) {
    case 1:
      return 'חודש';
    case 2:
      return 'חודשיים';
    default:
      return `${state.benefitAmount} חודשים`;
    }
  },
  promotion: state => state.benefitPromotion,
};

function isBenefitAndSubscriptionProviderMatch(benefit, rootGetters) {
  const isAppleBenefit = benefit.isAppleBenefit === true;
  const isAppleProvider = rootGetters['subscription/isApplePaymentProvider'];
  return isAppleProvider === isAppleBenefit;
}

const actions = {
  async initBenefits({ commit, rootGetters, dispatch }) {
    // initBenefits is dispatched from AuthenticateToken's non-awaited background Promise.all,
    // which races featureFlags/init in the parallel awaited block. Await it explicitly so the
    // flag check below is meaningful (the action is memoized so this is a no-op after the first).
    await dispatch('featureFlags/init', null, { root: true });
    const fetchBenefit = rootGetters['featureFlags/cashflowHomeBillingReadBenefits']
      ? BenefitsApi.getActiveBenefitFromCashflowHome()
      : BenefitsApi.getActiveBenefit();
    const [
      benefit,
      registeredUsingHeverPromotion,
    ] = await Promise.all([
      fetchBenefit,
      PromotionsApi.isRegisteredUsingHeverPromotion(),
    ]);
    if (!_.isEmpty(benefit)) {
      // Make sure we got update subscriptions
      await dispatch('subscription/fetchSubscription', null, { root: true });
      if (isBenefitAndSubscriptionProviderMatch(benefit, rootGetters)) {
        commit('setBenefit', benefit);
      }
    }
    if (registeredUsingHeverPromotion) {
      commit('setIsRegisteredUsingHeverPromotion', registeredUsingHeverPromotion);
    }
  },
  async redeemBenefit({ state }) {
    await BenefitsApi.redeemBenefit(state.benefitId);
    Segment.trackUserGot('BenefitRedeemed', { benefitType: state.benefitType, benefitAmount: state.benefitAmount });
  },
  async createBenefit({ dispatch }, promoCode) {
    const result = await BenefitsApi.createBenefitByPromoCode(promoCode);
    await dispatch('initBenefits');
    dispatch('fetchHeverBenefit', { force: true }).catch(() => {});
    return result;
  },
  async createBenefitByPromoSource({ dispatch }, { promoSource }) {
    const promotion = await PromotionsApi.getPromotionForSource(promoSource);
    return await dispatch('createBenefit', promotion.promoCode);
  },
  async fetchHeverBenefit({ state, commit }, { force = false } = {}) {
    if (!force && state.hasHeverBenefit !== null) {
      return;
    }
    heverBenefitRequestId += 1;
    const requestId = heverBenefitRequestId;
    const { hasHeverBenefit } = await BenefitsApi.hasHeverBenefit();
    if (requestId === heverBenefitRequestId) {
      commit('setHasHeverBenefit', hasHeverBenefit);
    }
  },
  resetHeverBenefit({ commit }) {
    heverBenefitRequestId += 1;
    commit('setHasHeverBenefit', null);
  },
};

const mutations = {
  setBenefit(state, benefit) {
    state.benefitId = benefit._id;
    state.benefitType = benefit.benefitType;
    state.benefitAmount = benefit.benefitAmount;
    state.benefitPercentage = benefit.benefitPercentage;
    state.benefitPrice = benefit.benefitPrice;
    state.subscriptionType = benefit.subscriptionType;
    state.benefitPromotion = benefit.promotion;
    state.redeemed = benefit.redeemed;
    state.isAppleBenefit = benefit.isAppleBenefit === true;
  },
  setBenefitId(state, benefitId) {
    state.benefitId = benefitId;
  },
  setBenefitType(state, benefitType) {
    state.benefitType = benefitType;
  },
  setBenefitAmount(state, benefitAmount) {
    state.benefitAmount = benefitAmount;
  },
  setBenefitPromotion(state, benefitPromotion) {
    state.benefitPromotion = benefitPromotion;
  },
  setBenefitPercentage(state, benefitPercentage) {
    state.benefitPercentage = benefitPercentage;
  },
  setBenefitPrice(state, benefitPrice) {
    state.benefitPrice = benefitPrice;
  },
  setSubscriptionType(state, subscriptionType) {
    state.subscriptionType = subscriptionType;
  },
  setIsRegisteredUsingHeverPromotion(state, registeredUsingHeverPromotion) {
    state.registeredUsingHeverPromotion = registeredUsingHeverPromotion;
  },
  setBenefitRedeemed(state, redeemed) {
    state.redeemed = redeemed;
  },
  setIsAppleBenefit(state, isAppleBenefit) {
    state.isAppleBenefit = isAppleBenefit === true;
  },
  setBenefitPresented(state, benefitPresented) {
    state.benefitPresented = benefitPresented;
  },
  setHasHeverBenefit(state, hasHeverBenefit) {
    state.hasHeverBenefit = hasHeverBenefit;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
